import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { CHALLENGE_TYPE_LABELS } from '@/components/challenges/challengeKinds';
import type { Challenge } from '@/config/objects/challenges';

const PICKER_PAGE_LIMIT = 100;
const ALL_TYPES = 'all';

export interface ChallengePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen library challenge ids; resolves when copied. */
  onAdd: (libraryChallengeIds: string[]) => Promise<void>;
  /** Ids of library challenges already copied into the event (shown as added). */
  existingSourceIds: ReadonlySet<string>;
  groupName?: string;
}

/**
 * Library challenge picker with search, type filter, and checkbox
 * multi-select — feeding the bulk-copy endpoint so an admin can pull a whole
 * set of challenges into a group at once.
 */
export function ChallengePickerDialog({
  open,
  onOpenChange,
  onAdd,
  existingSourceIds,
  groupName,
}: ChallengePickerDialogProps) {
  const apiClient = useApiClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, isError } = useQuery<{ data: Challenge[] }>({
    queryKey: ['Challenge', 'picker'],
    queryFn: () => apiClient.post('/challenges/list', { page: 1, limit: PICKER_PAGE_LIMIT }),
    enabled: open,
  });

  const challenges = useMemo(() => {
    const list = data?.data ?? [];
    const query = search.trim().toLowerCase();
    return list.filter((challenge) => {
      if (typeFilter !== ALL_TYPES && challenge.type !== typeFilter) return false;
      if (query && !challenge.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [data?.data, search, typeFilter]);

  const toggle = (id: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await onAdd([...selected]);
      setSelected(new Set());
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSelected(new Set());
        onOpenChange(next);
      }}
    >
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add challenges{groupName ? ` to ${groupName}` : ''}</DialogTitle>
          <DialogDescription>
            Pick from your challenge library — select several and add them all at once.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search challenges…"
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>All types</SelectItem>
              {Object.entries(CHALLENGE_TYPE_LABELS).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="min-h-0 flex-1 divide-y overflow-y-auto rounded-lg border">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 5 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : isError ? (
            // A failed load must not read as "no results".
            <p className="p-6 text-center text-sm text-muted-foreground">
              Couldn't load the challenge library — close the dialog and try again.
            </p>
          ) : challenges.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No challenges match — try a different search, or create one in the Challenge Library.
            </p>
          ) : (
            challenges.map((challenge) => {
              const alreadyAdded = existingSourceIds.has(challenge.id);
              return (
                <label
                  key={challenge.id}
                  className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 ${
                    alreadyAdded ? 'opacity-50' : ''
                  }`}
                >
                  <Checkbox
                    checked={selected.has(challenge.id)}
                    disabled={alreadyAdded}
                    onCheckedChange={() => toggle(challenge.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {challenge.name}
                    </span>
                    <span className="font-mono text-[0.6875rem] tracking-wide text-muted-foreground">
                      {CHALLENGE_TYPE_LABELS[challenge.type] ?? challenge.type} · {challenge.points}{' '}
                      pts
                    </span>
                  </span>
                  {alreadyAdded ? <Badge variant="outline">Added</Badge> : null}
                </label>
              );
            })
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <p className="font-mono text-xs tracking-wide text-muted-foreground">
            {selected.size} selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={selected.size === 0} isLoading={submitting}>
              Add {selected.size > 0 ? selected.size : ''} challenge{selected.size === 1 ? '' : 's'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
